import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { Card, formatWhen, G_DARK } from "../components/Shared";
import { useDisplayTimezone } from "../portalConfig";

/**
 * Automatic deletion of archived projects.
 *
 * The most destructive setting in the portal: one number here deletes every
 * file of every archived project past it, across the whole facility, with no
 * copy anywhere. So the screen is built around showing what it would take
 * rather than around setting it — the list of projects by name is the point,
 * and a count would not be enough.
 *
 * A group admin sees this and can ask for it; only an admin can change it.
 */
export default function RetentionPanel({ groupId, canEdit }) {
  const [state, setState]   = useState(null);
  const [due, setDue]       = useState(null);
  const [error, setError]   = useState(null);
  const zone = useDisplayTimezone();

  const load = useCallback(() => {
    if (!groupId) return;
    Promise.all([
      api.get(`/api/groups/${groupId}/retention`),
      api.get(`/api/groups/${groupId}/projects/clearable`),
    ])
      .then(([s, d]) => {
        setState(s);
        setDue(d);
        setError(null);
      })
      .catch(e => setError(e.message));
  }, [groupId]);

  useEffect(load, [load]);

  if (!state) return <Card><h3 style={{ margin: 0, fontSize: 16 }}>Retention</h3></Card>;

  const on = !!state.project_retention_days;

  return (
    <Card>
      <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Retention</h3>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
        Archived projects can be deleted automatically once they are old enough — the records{" "}
        <strong>and the files in storage</strong>. Off by default, and never sooner than{" "}
        {state.min_days} {state.min_days === 1 ? "day" : "days"} after archiving.
      </p>

      {error && <div role="alert" style={{ marginBottom: 12, color: "#dc2626", fontSize: 14 }}>{error}</div>}

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {on ? `${state.project_retention_days} days` : "Off"}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Current policy</div>
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{state.archived_projects}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Archived projects</div>
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: due?.due?.length ? "#b91c1c" : "#374151" }}>
            {due?.due?.length ?? 0}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Would go tonight</div>
        </div>
      </div>

      {/* By name, not by count. Nobody should approve something irreversible
          from a number alone. */}
      {due?.due?.length > 0 && (
        <div style={{ marginBottom: 16, padding: "10px 12px", borderRadius: 8,
                      background: "#fef2f2", border: "1px solid #fecaca" }}>
          <strong style={{ fontSize: 13, color: "#991b1b" }}>
            These would be deleted, with their files:
          </strong>
          <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 13 }}>
            {due.due.map(p => (
              <li key={p.id}>
                {p.name} — archived {formatWhen(p.archived_at, { zone, withTime: false })}
                {p.archive_reason ? ` (${p.archive_reason})` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* **Read-only, for everybody, since 2026-09-09.** The number is one
          setting for the whole facility now and lives in Admin Management, so
          the control that used to be here would have targeted a route that no
          longer exists. What stays is the part a group admin actually needs:
          the policy, what is archived, and exactly which projects would go
          tonight — by name, because a count is not enough for something
          irreversible. */}
      <div style={{ padding: "10px 12px", borderRadius: 8, background: "#f9fafb",
                    border: "1px solid #e5e7eb" }}>
        <p style={{ margin: 0, fontSize: 13, color: "#374151" }}>
          <strong>This is the whole facility's policy, and only an administrator sets it.</strong>{" "}
          One number deletes every file of every archived project past it, with no copy anywhere
          and no per-project confirmation — so it is one decision, in one place, taken by somebody
          who is not the person whose projects go.
        </p>
        <p style={{ margin: "8px 0 0", fontSize: 13, color: "#6b7280" }}>
          {canEdit
            ? "Set it under Management → Portal settings."
            : "You can archive projects, and you can ask for this to be set up or altered:"}{" "}
          {!canEdit && (
            <a href={`mailto:?subject=${encodeURIComponent("Retention policy")}`
                    + `&body=${encodeURIComponent(
                        `Please ${on ? "change" : "set up"} the retention policy.\n\n`
                        + `Current policy: ${on ? `${state.project_retention_days} days` : "off"}\n`
                        + `Archived projects in our group: ${state.archived_projects}\n\n`
                        + `Requested: `)}`}
               style={{ color: G_DARK }}>
              email an administrator
            </a>
          )}
        </p>
      </div>

    </Card>
  );
}
